import axios from 'axios'
import { API_ENDPOINTS } from '../../constants/index.js'
import { authContextRef } from '../authContextRef.js'
import { USE_JWT_COOKIE } from '../authConfig.js'

// Use environment variable for API base URL, fallback to relative path for development
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/pix/api'

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: USE_JWT_COOKIE,
  headers: {
    'Content-Type': 'application/json;charset=UTF-8',
    'Accept': 'application/json',
  },
})

// Request interceptor: add JWT token only when not using cookie (cookie is sent automatically)
apiClient.interceptors.request.use(
  (config) => {
    if (!USE_JWT_COOKIE) {
      const token = localStorage.getItem('token')
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor to handle auth errors
apiClient.interceptors.response.use(
  (response) => {
    return response
  },
  (error) => {
    console.error('API error:', error.response?.status, error.response?.data, error.message)

    if (error.response?.status === 406) {
      localStorage.removeItem('token')

      // dispatch error to AuthContext
      const { dispatch } = authContextRef.current || {}
      if (dispatch) {
        dispatch({
          type: 'AUTH_FAILURE',
          payload: { error: '認証トークンに問題があります。再ログインしてください。' }
        })
      }
    }
    return Promise.reject(error)
  }
)

export const authService = {
  async login(credentials) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.LOGIN, {
        companyLoginId: credentials.companyLoginId,
        mailAddress: credentials.mailAddress,
        password: credentials.password,
      })

      if (response.data.successFlg === '1') {
        // Store JWT token
        if (response.data.token) {
          if (!USE_JWT_COOKIE) {
            localStorage.setItem('token', response.data.token)
          }
        }
        // Update login time
        await this.updateLoginTime(response.data.userId)
      }

      return response.data
    } catch (error) {
      console.error('Login error:', error)
      throw error
    }
  },

  async updateLoginTime(userId) {
    try {
      await apiClient.post(API_ENDPOINTS.UPDATE_LOGIN_TIME, {
        pixUserId: userId,
      })
    } catch (error) {
      console.error('Update login time error:', error)
    }
  },

  async validateToken() {
    try {
      const response = await apiClient.get(API_ENDPOINTS.VALIDATE_TOKEN)
      return response.data
    } catch (error) {
      console.error('Token validation error:', error)
      throw error
    }
  },

  async logout() {
    if (USE_JWT_COOKIE) {
      try {
        await apiClient.post(API_ENDPOINTS.LOGOUT)
      } catch (error) {
        console.error('Logout error:', error)
      }
    }
    localStorage.removeItem('token')
  },

  async resetPassword(email) {
    try {
      const response = await apiClient.post('/api/resetPassword', {
        email: email,
      })
      return response.data
    } catch (error) {
      console.error('Reset password error:', error)
      throw error
    }
  },

  async changePassword(oldPassword, newPassword) {
    try {
      const response = await apiClient.post('/api/changePassword', {
        oldPassword: oldPassword,
        newPassword: newPassword,
      })
      return response.data
    } catch (error) {
      console.error('Change password error:', error)
      throw error
    }
  },

  async updateProfile(userData) {
    try {
      const response = await apiClient.put('/api/updateProfile', userData)
      return response.data
    } catch (error) {
      console.error('Update profile error:', error)
      throw error
    }
  },

  async getProfile() {
    try {
      const response = await apiClient.get('/api/getProfile')
      return response.data
    } catch (error) {
      console.error('Get profile error:', error)
      throw error
    }
  },
}

export default apiClient
